import { useCallback, useEffect, useMemo, useState } from "react"
import debounce from "lodash.debounce"
import { Link } from "react-router-dom"
import { Download, Loader2, Search, Upload, UserPlus } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Badge } from "@/components/ui/badge"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { supabase } from "@/lib/supabase"
import { toast } from "sonner"
import { usePermission } from "@/hooks/usePermission"
import { useOrgStore } from "@/stores/orgStore"
import { ImportContactsWizard } from "@/components/contacts/ImportContactsWizard"
import { RequireOrg } from "@/components/RequireOrg"
import { formatCurrency, formatRelativeTime } from "@/lib/format"
import { escapeCSVField, downloadCsv } from "@/lib/csv"

type DonationRow = { contact_id: string | null; amount: number; created_at: string }
type VolunteerRow = { contact_id: string; hours: number | null; activity_date: string }
type DonorTagRow = { contact_id: string; tag: string }
type DonorNoteRow = { contact_id: string }

export interface ContactWithStats {
  id: string
  name: string | null
  email: string
  tags: string[]
  totalDonated: number
  totalVolunteerHours: number
  lastActivityDate: string | null
  donationCount: number
}

const AMOUNT_FILTERS = [
  { value: "all", label: "All", minCents: 0 },
  { value: "100", label: "$100+", minCents: 10000 },
  { value: "500", label: "$500+", minCents: 50000 },
  { value: "1000", label: "$1,000+", minCents: 100000 },
] as const

const DATE_FILTERS = [
  { value: "all", label: "All time", daysBack: null, yearStart: false },
  { value: "30", label: "Last 30 days", daysBack: 30, yearStart: false },
  { value: "90", label: "Last 90 days", daysBack: 90, yearStart: false },
  { value: "year", label: "This year", daysBack: null, yearStart: true },
] as const

function generateContactCSV(contacts: ContactWithStats[]): string {
  const headers = [
    "Name",
    "Email",
    "Tags",
    "Total Donated",
    "Volunteer Hours",
    "Last Activity Date",
    "Donation Count",
  ]

  const rows = contacts.map((contact) => {
    const tags = contact.tags?.filter(Boolean).join(", ") || ""
    const totalDonated = (contact.totalDonated / 100).toFixed(2)
    const volunteerHours = contact.totalVolunteerHours?.toFixed(1) || "0"
    const lastActivity =
      contact.lastActivityDate != null
        ? new Date(contact.lastActivityDate).toISOString().split("T")[0]
        : ""
    const donationCount = contact.donationCount ?? 0

    return [
      escapeCSVField(contact.name ?? ""),
      escapeCSVField(contact.email ?? ""),
      escapeCSVField(tags),
      totalDonated,
      volunteerHours,
      lastActivity,
      String(donationCount),
    ]
  })

  const csvContent = [
    headers.join(","),
    ...rows.map((row) => row.join(",")),
  ].join("\n")

  return csvContent
}

export function ContactsPage() {
  return (
    <RequireOrg>
      <ContactsPageContent />
    </RequireOrg>
  )
}

function ContactsPageContent() {
  const { currentOrg, userOrgs } = useOrgStore()
  const { hasPermission: canViewFinancials } = usePermission("can_view_financials")
  const { hasPermission: canManageContacts } = usePermission("can_manage_contacts")
  const [importOpen, setImportOpen] = useState(false)
  const [contacts, setContacts] = useState<ContactWithStats[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState("")
  const [searchDebounced, setSearchDebounced] = useState("")
  const [tagFilter, setTagFilter] = useState<string>("all")
  const [amountFilter, setAmountFilter] = useState<string>("all")
  const [dateFilter, setDateFilter] = useState<string>("all")
  const [availableTags, setAvailableTags] = useState<string[]>([])

  const orgId = (currentOrg ?? userOrgs[0])?.id
  const [addDialogOpen, setAddDialogOpen] = useState(false)
  const [addName, setAddName] = useState("")
  const [addEmail, setAddEmail] = useState("")
  const [addSaving, setAddSaving] = useState(false)

  const handleAddContact = async () => {
    const email = addEmail.trim()
    const name = addName.trim()
    if (!email || !orgId) return
    setAddSaving(true)
    try {
      const { error } = await supabase.from("contacts").insert({
        org_id: orgId,
        email,
        name: name || null,
        source: "manual",
      })
      if (error) {
        if (error.code === "23505") {
          toast.error("A contact with that email already exists")
        } else {
          toast.error("Failed to add contact")
        }
        return
      }
      toast.success(`Contact added`)
      setAddDialogOpen(false)
      setAddName("")
      setAddEmail("")
      fetchContacts()
    } finally {
      setAddSaving(false)
    }
  }

  const debouncedSetSearch = useMemo(
    () =>
      debounce((value: string) => {
        setSearchDebounced(value.trim())
      }, 500),
    []
  )

  useEffect(() => {
    debouncedSetSearch(search)
    return () => debouncedSetSearch.cancel()
  }, [search, debouncedSetSearch])

  const fetchContacts = useCallback(async () => {
    if (!orgId) return
    setLoading(true)
    try {
      let contactsQuery = supabase
        .from("contacts")
        .select("id, name, email")
        .eq("org_id", orgId)

      if (searchDebounced) {
        // Notes are searched first so note-only matches make it into the
        // server-side contacts filter — filtering after the fetch can never
        // surface a contact the name/email filter already excluded.
        const notesRes = await supabase
          .from("donor_notes")
          .select("contact_id")
          .eq("org_id", orgId)
          .ilike("note", `%${searchDebounced}%`)
        const noteMatchIds = [
          ...new Set(
            ((notesRes.data ?? []) as DonorNoteRow[]).map((n) => n.contact_id)
          ),
        ]
        const orFilters = [
          `name.ilike.%${searchDebounced}%`,
          `email.ilike.%${searchDebounced}%`,
        ]
        if (noteMatchIds.length > 0) {
          orFilters.push(`id.in.(${noteMatchIds.join(",")})`)
        }
        contactsQuery = contactsQuery.or(orFilters.join(","))
      }

      const donationsPromise = canViewFinancials
        ? supabase
            .from("donations")
            .select("contact_id, amount, created_at")
            .eq("org_id", orgId)
            .eq("status", "succeeded")
        : Promise.resolve({ data: [] as DonationRow[], error: null })

      const [contactsRes, donationsRes, volunteerRes, tagsRes] = await Promise.all([
        contactsQuery,
        donationsPromise,
        supabase
          .from("volunteer_activities")
          .select("contact_id, hours, activity_date")
          .eq("org_id", orgId),
        supabase
          .from("donor_tags")
          .select("contact_id, tag")
          .eq("org_id", orgId),
      ])

      const contactRows = contactsRes.data ?? []
      const donations = (donationsRes.data ?? []) as DonationRow[]
      const volunteers = (volunteerRes.data ?? []) as VolunteerRow[]
      const tags = (tagsRes.data ?? []) as DonorTagRow[]

      if (contactsRes.error || !contactRows) {
        setContacts([])
        setLoading(false)
        return
      }

      const tagsByContactId = new Map<string, string[]>()
      tags.forEach((t) => {
        const arr = tagsByContactId.get(t.contact_id) ?? []
        if (!arr.includes(t.tag)) arr.push(t.tag)
        tagsByContactId.set(t.contact_id, arr)
      })

      const donatedByContactId = new Map<
        string,
        { total: number; lastDate: string; count: number }
      >()
      donations.forEach((d) => {
        if (!d.contact_id) return
        const cur = donatedByContactId.get(d.contact_id) ?? {
          total: 0,
          lastDate: "",
          count: 0,
        }
        cur.total += d.amount
        cur.count += 1
        if (d.created_at > cur.lastDate) cur.lastDate = d.created_at
        donatedByContactId.set(d.contact_id, cur)
      })

      const volunteerByContactId = new Map<string, { hours: number; lastDate: string }>()
      volunteers.forEach((v) => {
        const cur = volunteerByContactId.get(v.contact_id) ?? { hours: 0, lastDate: "" }
        cur.hours += v.hours ?? 0
        if (v.activity_date > cur.lastDate) cur.lastDate = v.activity_date
        volunteerByContactId.set(v.contact_id, cur)
      })

      const effectiveAmountFilter = canViewFinancials ? amountFilter : "all"
      const amountOpt = AMOUNT_FILTERS.find((a) => a.value === effectiveAmountFilter)
      const dateOpt = DATE_FILTERS.find((d) => d.value === dateFilter)
      const minCents = amountOpt?.minCents ?? 0
      let dateThreshold: string | null = null
      if (dateOpt?.yearStart) {
        const y = new Date().getFullYear()
        dateThreshold = `${y}-01-01`
      } else if (dateOpt?.daysBack != null) {
        const thresholdDate = new Date()
        thresholdDate.setDate(thresholdDate.getDate() - dateOpt.daysBack)
        dateThreshold = thresholdDate.toISOString().slice(0, 10)
      }

      let filtered = contactRows.filter((c) => {
        const donated = donatedByContactId.get(c.id)
        const totalCents = donated?.total ?? 0
        if (minCents > 0 && totalCents < minCents) return false

        const volunteer = volunteerByContactId.get(c.id)
        const lastDonation = donated?.lastDate ?? null
        const lastVolunteer = volunteer?.lastDate ?? null
        const lastActivity =
          !lastDonation && !lastVolunteer
            ? null
            : !lastDonation
              ? lastVolunteer
              : !lastVolunteer
                ? lastDonation
                : lastDonation > lastVolunteer
                  ? lastDonation
                  : lastVolunteer

        if (dateThreshold != null && lastActivity) {
          const activityDate = lastActivity.slice(0, 10)
          if (activityDate < dateThreshold) return false
        }

        if (tagFilter !== "all") {
          const contactTags = tagsByContactId.get(c.id) ?? []
          if (!contactTags.includes(tagFilter)) return false
        }
        return true
      })

      const allTags = new Set<string>()
      tags.forEach((t) => allTags.add(t.tag))
      setAvailableTags(["Donor", "Volunteer", ...Array.from(allTags)])

      const result: ContactWithStats[] = filtered.map((c) => {
        const donated = donatedByContactId.get(c.id)
        const volunteer = volunteerByContactId.get(c.id)
        const contactTags = tagsByContactId.get(c.id) ?? []
        const lastDonation = donated?.lastDate ?? null
        const lastVolunteer = volunteer?.lastDate ?? null
        const lastActivity =
          !lastDonation && !lastVolunteer
            ? null
            : !lastDonation
              ? lastVolunteer
              : !lastVolunteer
                ? lastDonation
                : lastDonation! > lastVolunteer!
                  ? lastDonation
                  : lastVolunteer

        return {
          id: c.id,
          name: c.name,
          email: c.email,
          tags: contactTags,
          totalDonated: donated?.total ?? 0,
          totalVolunteerHours: volunteer?.hours ?? 0,
          lastActivityDate: lastActivity,
          donationCount: donated?.count ?? 0,
        }
      })

      result.sort((a, b) => {
        const aDate = a.lastActivityDate ?? ""
        const bDate = b.lastActivityDate ?? ""
        return bDate.localeCompare(aDate)
      })

      setContacts(result)
    } catch (err) {
      console.error("Contacts fetch error:", err)
      setContacts([])
    } finally {
      setLoading(false)
    }
  }, [orgId, searchDebounced, tagFilter, amountFilter, dateFilter, canViewFinancials])

  useEffect(() => {
    fetchContacts()
  }, [fetchContacts])

  const handleExportCSV = () => {
    if (contacts.length === 0) {
      toast.error("No contacts to export with current filters")
      return
    }
    if (!canViewFinancials) {
      toast.error("You don't have permission to export financial data")
      return
    }
    const csv = generateContactCSV(contacts)
    const dateStr = new Date().toISOString().split("T")[0]
    const org = currentOrg ?? userOrgs[0]
    const filename = `contacts-${org?.slug ?? "export"}-${dateStr}.csv`
    downloadCsv(csv, filename)
    toast.success(`Exported ${contacts.length} contacts to CSV`)
  }

  return (
    <div className="bg-background">
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="text-lg font-semibold">Contacts &amp; Volunteers</h2>
          <div className="flex items-center gap-2">
            {canManageContacts && (
              <Button size="sm" variant="outline" onClick={() => setImportOpen(true)}>
                <Upload className="mr-2 h-4 w-4" />
                Import CSV
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={handleExportCSV}
              disabled={loading || contacts.length === 0 || !canViewFinancials}
              title={
                contacts.length === 0
                  ? "No contacts to export with current filters"
                  : undefined
              }
            >
              <Download className="mr-2 h-4 w-4" />
              Export CSV
            </Button>
            <Button size="sm" onClick={() => setAddDialogOpen(true)}>
              <UserPlus className="mr-2 h-4 w-4" />
              Add Contact
            </Button>
          </div>
        </div>

        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search by name, email, or notes..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <Select value={tagFilter} onValueChange={setTagFilter}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Tag" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All tags</SelectItem>
                {availableTags.map((tag) => (
                  <SelectItem key={tag} value={tag}>
                    {tag}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {canViewFinancials && (
              <Select value={amountFilter} onValueChange={setAmountFilter}>
                <SelectTrigger className="w-[130px]">
                  <SelectValue placeholder="Amount" />
                </SelectTrigger>
                <SelectContent>
                  {AMOUNT_FILTERS.map((a) => (
                    <SelectItem key={a.value} value={a.value}>
                      {a.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Select value={dateFilter} onValueChange={setDateFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Date" />
              </SelectTrigger>
              <SelectContent>
                {DATE_FILTERS.map((d) => (
                  <SelectItem key={d.value} value={d.value}>
                    {d.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="rounded-md border">
          {loading ? (
            <div className="flex items-center justify-center gap-2 p-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Loading contacts...</span>
            </div>
          ) : contacts.length === 0 ? (
            <p className="p-8 text-center text-muted-foreground">
              No contacts yet. Add one manually or they'll appear automatically from donations.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Tags</TableHead>
                    {canViewFinancials && (
                      <TableHead className="text-right">Total Donated</TableHead>
                    )}
                    <TableHead className="text-right">Volunteer Hours</TableHead>
                    <TableHead>Last Activity</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {contacts.map((c) => (
                    <TableRow key={c.id}>
                      <TableCell>
                        <Link
                          to={`/dashboard/contacts/${c.id}`}
                          className="font-medium text-primary hover:underline"
                        >
                          {c.name || c.email || "—"}
                        </Link>
                      </TableCell>
                      <TableCell className="text-muted-foreground">{c.email}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {c.tags.slice(0, 3).map((tag) => (
                            <Badge key={tag} variant="secondary" className="text-xs">
                              {tag}
                            </Badge>
                          ))}
                          {c.tags.length > 3 && (
                            <Badge variant="outline" className="text-xs">
                              +{c.tags.length - 3} more
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      {canViewFinancials && (
                        <TableCell className="text-right font-medium">
                          {formatCurrency(c.totalDonated, { digits: 0 })}
                        </TableCell>
                      )}
                      <TableCell className="text-right">
                        {c.totalVolunteerHours > 0 ? c.totalVolunteerHours : "—"}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatRelativeTime(c.lastActivityDate)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>

      <ImportContactsWizard
        open={importOpen}
        onOpenChange={setImportOpen}
        orgId={orgId}
        onComplete={fetchContacts}
      />

      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Contact</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid gap-1.5">
              <Label htmlFor="contact-name">Name</Label>
              <Input
                id="contact-name"
                placeholder="Jane Smith"
                value={addName}
                onChange={(e) => setAddName(e.target.value)}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="contact-email">
                Email <span className="text-destructive">*</span>
              </Label>
              <Input
                id="contact-email"
                type="email"
                placeholder="jane@example.com"
                value={addEmail}
                onChange={(e) => setAddEmail(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && addEmail.trim()) handleAddContact()
                }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddContact} disabled={!addEmail.trim() || addSaving}>
              {addSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Add Contact
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
